/**
 * ResearchTurnView - one turn.
 *
 * The renderer is chosen from the envelope's `intent` via
 * `ResearchResponseView`; nothing here sniffs JSON keys off a text field to
 * guess a shape. A turn is in exactly one of four states, distinguishable by
 * type rather than by a boolean soup:
 *
 * The classified INTENT drives which renderer runs and whether the answer is
 * a document - it is never printed. A "detected: Overview" chip used to sit
 * above the answer, which read as debug output leaking into the transcript.
 *
 *   streaming  → the engine's narration + the working line
 *                  (+ the shape's skeleton once known)
 *   answered   → the typed renderer
 *   unhydrated → the SQL summary, with the shape's skeleton
 *   failed     → the error, plainly
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
// Version switching only - the RUNNING turn's loader lives in WorkingIndicator.
import BrandLoader from '../../shared/brand/BrandLoader';
import ResearchResponseView from '../ResearchResponseView';
import IntentSkeleton from '../IntentSkeleton';
import { CitationOpenRequest } from '../types';
import {
  AssistantTurn,
  ResearchTurn,
  UserTurn,
  failureReason,
  isAssistantTurn,
  turnDisplayState,
} from '../turns';
import { artifactPreview, artifactShapeIsSettled, artifactTitle, turnIsArtifact } from '../artifacts';
import WorkingIndicator from './WorkingIndicator';
import AnswerReveal from './AnswerReveal';
import ArtifactMessage from './ArtifactMessage';
import TurnAttachments from './TurnAttachments';
import TurnActions from './TurnActions';

interface Props {
  turn: ResearchTurn;
  onOpenCitation: (url: string) => void;
  onOpenCitationRequest?: (request: CitationOpenRequest) => void;
  /** History: fetch this turn's blob once it is on screen. */
  onHydrate?: (messageUuid: string) => void;
  /** Document-shaped outputs open in the pane instead of rendering inline. */
  onOpenArtifact?: (turnId: string) => void;
  /** This turn's artifact is the one currently in the pane. */
  isArtifactOpen?: boolean;
  artifactVersion?: number;
  artifactVersionCount?: number;
  /** Send a suggested follow-up question. */
  onFollowUp?: (text: string) => void;
  /**
   * Citations from the ANSWER to this user message.
   *
   * An uploaded file is only openable through the citation the engine made
   * of it, so a user turn needs its reply's citations to offer the action.
   */
  answerCitations?: any[];
  /** Open an uploaded file's ingested copy. */
  onOpenAttachment?: (citation: any) => void;
  /** Re-run this answer, optionally with an edited question. */
  onRerun?: (messageUuid: string, query?: string) => void;
  /** Show a different version of this answer. */
  onSelectVersion?: (messageUuid: string, version: number) => void;
  /** Rate this answer. */
  onRate?: (messageUuid: string, rating: 'up' | 'down' | null) => void;
  /** Refine this answer, optionally saying how. */
  onRefine?: (messageUuid: string, instruction?: string) => void;
  /** Load this turn's question into the main composer. */
  onEdit?: (messageUuid: string, query: string) => void;
}

const UserBubble: React.FC<{
  text: string;
  attachments?: UserTurn['attachments'];
  citations?: any[];
  onOpenAttachment?: (citation: any) => void;
}> = ({ text, attachments, citations, onOpenAttachment }) => (
  <div className="flex flex-col items-end mb-3" data-testid="turn-user">
    {/* An empty message with only a file attached is a real case - the
        bubble is skipped rather than rendered blank. */}
    {text.trim() !== '' && (
      <div className="py-1.5 px-3 rounded-lg rounded-br-none bg-surface shadow-sm
                      max-w-2xl text-sm text-fg-body whitespace-pre-wrap">
        {text}
      </div>
    )}
    <TurnAttachments
      attachments={attachments}
      citations={citations}
      onOpen={onOpenAttachment}
    />
  </div>
);

const AssistantBubble: React.FC<Props & { turn: AssistantTurn }> = ({
  turn,
  onOpenCitation,
  onOpenCitationRequest,
  onHydrate,
  onOpenArtifact,
  isArtifactOpen = false,
  artifactVersion,
  artifactVersionCount,
  onFollowUp,
  onRerun,
  onSelectVersion,
  onRate,
  onRefine,
  onEdit,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);

  /* Lazy hydration: a history turn renders from its SQL summary, and its blob
     is fetched only once the turn is actually on screen. */
  useEffect(() => {
    const node = rootRef.current;
    const uuid = turn.messageUuid;
    if (!node || !uuid || !onHydrate) return;
    if (turn.hydration !== 'pending') return;

    if (typeof IntersectionObserver === 'undefined') {
      onHydrate(uuid);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          onHydrate(uuid);
          observer.disconnect();
        }
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [turn.messageUuid, turn.hydration, onHydrate]);

  const state = turnDisplayState(turn);
  const error = turn.live?.error;
  /**
   * The persisted copy wins once it exists: the live list is the same lines,
   * but the blob is what a reload reads, so preferring it keeps one source
   * of truth and cannot double up.
   */
  const interim = turn.response?.interim?.length ? turn.response.interim : (turn.interim || []);
  /**
   * The engine's progress phrases for this question - same two sources as
   * the narration, for the same reason: the blob is what a reload of a
   * still-running turn reads back.
   */
  const cues = turn.response?.cues?.length ? turn.response.cues : (turn.cues || []);

  /**
   * Document-shaped output: the transcript keeps a card and the pane holds
   * the document. The intent is known from `research.intent` before any
   * content exists, so the card stands in from early in the run - but only
   * once the SHAPE is actually settled. The engine may still hold the turn to
   * ask whether the user wants a document at all, and a card that appears
   * before that question is asked has answered it for them. See
   * `artifactShapeIsSettled`; until then the working line above is the whole
   * of what this turn shows.
   */
  const artifactIntent = turn.response?.intent ?? turn.intent;
  const rendersAsArtifact =
    !!onOpenArtifact &&
    turnIsArtifact(turn) &&
    artifactShapeIsSettled(turn) &&
    (state === 'streaming' || state === 'answered' || state === 'unhydrated');

  return (
    <div className="flex mb-5" data-testid="turn-assistant" data-state={state}
         // The transcript scrolls a freshly-answered turn's TOP into view and
         // needs to find this node to do it.
         data-turn-id={turn.id}
         data-hydration={turn.hydration} ref={rootRef}>
      {/*
        NO AVATAR GUTTER AT ALL - in any state.

        This slot held a 28px BrandLoader while running (a second loader, next
        to the one in the working line) and the PONS mark once settled. Both
        are gone: the answer is already unmistakably the assistant's - it is
        the only thing on the left, opposite the user's right-aligned bubble —
        and a 40px column repeating that on every turn only narrowed the
        content it was labelling.

        Removing it in BOTH states is what makes the width constant: an answer
        landing no longer un-indents itself as the run ends. The only spinner
        during a run is the small mark inside the working line, at the start
        of the content rather than beside it.
      */}
      <div className="min-w-0 flex-1 max-w-3xl">
        {/* The engine's own conversational lines during the run. Restored
            from the blob after a reload, so a reopened turn reads as it did
            live. Never invented here - this renders nothing when the engine
            sent nothing. */}
        {interim.length > 0 && (
          <div className="mb-2 space-y-1" data-testid="turn-interim">
            {interim.map((line, i) => (
              <p
                key={`${i}-${line}`}
                data-testid="interim-message"
                className="text-sm leading-relaxed text-fg-body reveal-item"
                style={{ '--reveal-delay': `${Math.min(i, 6) * 40}ms` } as React.CSSProperties}
              >
                {line}
              </p>
            ))}
          </div>
        )}

        {/* One working line, for EVERY intent, artifact or not - the mark
            alone at first, then the engine's own phrases for this question
            once they land, and the generic vocabulary only if they never do.
            It unmounts with the answer, leaving no completion summary. */}
        {state === 'streaming' && <WorkingIndicator cues={cues} />}

        {/*
          ONE branch runs. `streaming` is checked first and wins, so an
          in-flight turn can never fall through to an answer - least of all
          the empty-result copy, which is reserved for a COMPLETED run that
          genuinely found nothing.
        */}
        {rendersAsArtifact && artifactIntent && (
          <ArtifactMessage
            intent={artifactIntent}
            title={artifactTitle(turn)}
            preview={artifactPreview(turn)}
            isStreaming={state === 'streaming'}
            isOpen={isArtifactOpen}
            version={artifactVersion}
            versionCount={artifactVersionCount}
            leadIn={turn.response?.leadIn}
            closing={turn.response?.closing}
            followUp={turn.response?.followUp}
            onOpen={() => onOpenArtifact?.(turn.id)}
            onFollowUp={onFollowUp}
          />
        )}

        {/*
          SWITCHING VERSIONS - in place, at a stable height.

          Each version is its own blob, so a switch is a fetch. Swapping the
          body for a skeleton resized the message and the whole transcript
          jumped, which reads worse than the wait it was explaining. So the
          existing answer STAYS, dimmed and inert, under a small spinner: the
          container keeps its height, nothing reflows, and the new version
          replaces it a moment later.

          The dim is what stops the old text being mistaken for the new
          version - it plainly reads as "not this yet" - which is the concern
          the skeleton was there to address, without the resize.
        */}
        <div
          className={clsx('relative', turn.versionLoading && 'min-h-[3rem]')}
          data-testid={turn.versionLoading ? 'version-loading' : undefined}
          aria-busy={turn.versionLoading || undefined}
        >
          <div
            className={clsx(
              'transition-opacity duration-200 ease-slide',
              turn.versionLoading && 'opacity-40 pointer-events-none select-none'
            )}
            data-testid="turn-body"
          >

        {!rendersAsArtifact && state === 'streaming' && (
          /* No separate "connecting" line: the working indicator above
             already covers the gap before the first engine word. */
          <IntentSkeleton intent={turn.intent ?? null} />
        )}

        {!rendersAsArtifact && state === 'answered' && turn.response && (
          /*
            The answer LANDS rather than appears.

            Gated on `turn.liveArrival`, NOT on `turn.live`. Reading liveness
            off the channel was wrong in exactly the case that matters: the
            answer usually reaches the client through the blob-fetch
            reconcile, and every settle path clears `live` in the same update
            that sets `response` - precisely so nothing reads as "still
            running". By the time this branch renders there is no channel
            left to ask, so the reveal never fired.

            `liveArrival` is set where a run is OPENED and never cleared, so
            it still answers "did the user watch this arrive?" after the
            settle. Reopening a finished chat rebuilds its turns from SQL,
            which never carry it - so history renders flat, as it should.

            A version swap is excluded: that answer is a fetch, not an
            arrival, and re-animating under the dimmed overlay would read as
            a glitch.
          */
          <AnswerReveal active={!!turn.liveArrival && !error && !turn.versionLoading}>
            <ResearchResponseView
              response={turn.response}
              onOpenCitation={onOpenCitation}
              onOpenCitationRequest={onOpenCitationRequest}
            />
          </AnswerReveal>
        )}

        {state === 'empty' && (
          <div data-testid="turn-empty" className="text-sm text-fg-muted">
            <p>{t('No relevant documents were found for this question.')}</p>
            <p className="mt-1 text-xs">
              {t('Try widening the selected sources, or rephrasing the question.')}
            </p>
          </div>
        )}

          </div>

          {turn.versionLoading && (
            // Centred over the dimmed answer, not in the flow, so it adds no
            // height of its own.
            <div
              className="absolute inset-0 flex items-center justify-center"
              data-testid="version-loading-overlay"
            >
              <BrandLoader size="sm" />
            </div>
          )}
        </div>

        {/*
          Failure, re-run, edit and versions - ONE component.

          A failed turn used to print a bare line of red text here while the
          rerun affordance sat separately below, and a refused re-run printed
          a third thing. They are one situation to the reader, so
          `failureReason` decides the text and `TurnActions` renders it with
          Rerun promoted. Re-run is withheld only while the turn is actually
          producing an answer - that is the 409 the user should never be
          offered - but the version toggle stays available throughout.
        */}
        <TurnActions
          turn={turn}
          onRerun={onRerun}
          onSelectVersion={onSelectVersion}
          onRate={onRate}
          onRefine={onRefine}
          onEdit={onEdit}
          disabled={state === 'streaming'}
          failure={state === 'streaming' ? undefined : failureReason(turn)}
        />

        {!rendersAsArtifact && state === 'unhydrated' && !turn.versionLoading && (
          <>
            {/*
              The SQL summary is a real answer already, so an OLDER turn shows
              it while its blob is fetched on view.

              The turn being fetched EAGERLY (the newest, on chat open) skips
              it: its document is already on the way, and painting the plain
              summary first only to replace it a moment later is the
              formatting flicker. A skeleton reads as loading; swapped text
              reads as a glitch.
            */}
            {turn.summary && turn.hydration !== 'loading' && (
              <p className="text-sm leading-relaxed text-fg-body whitespace-pre-wrap">
                {turn.summary}
              </p>
            )}
            <IntentSkeleton intent={turn.intent ?? null} />
          </>
        )}
      </div>
    </div>
  );
};

const ResearchTurnView: React.FC<Props> = (props) => {
  if (!isAssistantTurn(props.turn)) {
    return (
      <UserBubble
        text={props.turn.text}
        attachments={props.turn.attachments}
        citations={props.answerCitations}
        onOpenAttachment={props.onOpenAttachment}
      />
    );
  }
  return <AssistantBubble {...props} turn={props.turn} />;
};

export default ResearchTurnView;
